import { useMemo } from "react";
import { Link, useRoute } from "wouter";
import { ChevronLeft, Trophy, User as UserIcon } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useGames, usePlayerScoringStats, useRoster } from "@/lib/queries";
import type { Game, Player, PlayerScoringStats } from "@/lib/types";

// Drilled-in view of a single player's performance. Reached from
// the leaderboards page (rank rows) and from the PlayersList (each
// player name links here). All stats are computed client-side from
// useGames({playerId}) — keeps a single source of truth and means
// the page auto-refreshes when new games complete + React Query
// invalidates. Server-side caching can land later when org game
// counts make the join expensive.
//
// Sections:
//   - header: name, avatar, @slug
//   - summary tiles: games, W-L, win %, accuracy (pts/game), point diff
//   - overall W/L bar (visual reinforcement)
//   - per-opponent head-to-head bars (who they dominate vs struggle against)
//   - accuracy trend sparkline (their pts/game over recent games)
//   - recent games table with links into GameDetail

export default function PlayerProfile() {
  const [, params] = useRoute("/dashboard/players/:slug");
  const slug = params?.slug ?? null;

  // Resolve player by slug from the org roster. useRoster is cheap
  // (already cached by other surfaces) and gives us avatarUrl /
  // location / userId in one shot.
  const { data: roster = [], isLoading: rosterLoading } = useRoster();
  const player = roster.find((p) => p.slug === slug) ?? null;

  // Pull this player's games. Server filters by playerId via
  // game_players join so we don't drag the whole org's history.
  const { data: games = [], isLoading: gamesLoading } = useGames({
    playerId: player?.id,
  });

  // Loading + 404 states up front before doing any number-crunching.
  if (rosterLoading || gamesLoading) {
    return <div className="p-8 text-muted-foreground">Loading…</div>;
  }
  if (!player) {
    return (
      <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-3xl mx-auto space-y-4">
        <Link
          href="/dashboard/leaderboards"
          className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
        >
          <ChevronLeft size={12} /> Leaderboards
        </Link>
        <div className="rounded-md border border-border bg-muted/20 p-6 text-sm text-muted-foreground">
          Player not found in this org.
        </div>
      </div>
    );
  }

  return <PlayerProfileBody player={player} games={games} />;
}

function PlayerProfileBody({ player, games }: { player: Player; games: Game[] }) {
  // Offensive/defensive output from both-sides scoring (server-side
  // aggregation over game_frames gross). rounds=0 → the card is hidden.
  const { data: scoring } = usePlayerScoringStats(player.id);

  // Completed LIVE games only — virtual/practice games (vs the Ghost AI or
  // a friend in 3D) have their own stats and shouldn't inflate the
  // competitive record; live + voided games have no final outcome. Sort
  // oldest → newest so the accuracy trend reads left-to-right "over time".
  const completed = useMemo(
    () =>
      games
        .filter(
          (g) =>
            g.status === "complete" && !g.isVirtual && g.winningTeam != null,
        )
        .slice()
        .sort(
          (a, b) =>
            new Date(a.playedOn).getTime() - new Date(b.playedOn).getTime(),
        ),
    [games],
  );

  // Per-game accounting from this player's perspective.
  // teamOf returns 1 if the player is on team1, 2 if team2, null
  // otherwise (shouldn't happen since we filter by playerId).
  function teamOf(g: Game): 1 | 2 | null {
    if (g.team1Players.some((p) => p.id === player.id)) return 1;
    if (g.team2Players.some((p) => p.id === player.id)) return 2;
    return null;
  }

  let wins = 0;
  let losses = 0;
  let pointsFor = 0;
  let pointsAgainst = 0;
  const perOppMap = new Map<
    string,
    { name: string; wins: number; losses: number }
  >();
  const accuracySeries: number[] = [];
  for (const g of completed) {
    const t = teamOf(g);
    if (t == null) continue;
    const myPts = t === 1 ? g.team1Score : g.team2Score;
    const theirPts = t === 1 ? g.team2Score : g.team1Score;
    pointsFor += myPts;
    pointsAgainst += theirPts;
    if (g.winningTeam === t) wins += 1;
    else if (g.winningTeam != null && g.winningTeam !== t) losses += 1;

    // pts/game as the accuracy proxy (consistent with dashboard)
    accuracySeries.push(myPts);

    // Opponent grouping. For singles, the opp is the other team's
    // sole player; for doubles, we join the two names with " & " so
    // duo identities show up as a stable opponent key.
    const oppTeam = t === 1 ? g.team2Players : g.team1Players;
    const oppName = oppTeam.map((p) => p.name).join(" & ") || "Unknown";
    let row = perOppMap.get(oppName);
    if (!row) {
      row = { name: oppName, wins: 0, losses: 0 };
      perOppMap.set(oppName, row);
    }
    if (g.winningTeam === t) row.wins += 1;
    else if (g.winningTeam != null && g.winningTeam !== t) row.losses += 1;
  }

  const totalGames = wins + losses;
  const winPct = totalGames > 0 ? wins / totalGames : 0;
  const accuracy =
    completed.length > 0 ? pointsFor / completed.length : 0;
  const pointDiff = pointsFor - pointsAgainst;
  const perOpp = Array.from(perOppMap.values())
    .filter((r) => r.wins + r.losses > 0)
    .sort((a, b) => b.wins + b.losses - (a.wins + a.losses));
  // Recent games: most recent 10. Already sorted oldest→newest above
  // so we slice from the end and reverse for the table.
  const recent = completed.slice(-10).reverse();
  const initials = player.name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-4xl mx-auto space-y-6">
      <header className="space-y-3">
        <Link
          href="/dashboard/leaderboards"
          className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
        >
          <ChevronLeft size={12} /> Leaderboards
        </Link>
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-full bg-muted overflow-hidden border border-border flex items-center justify-center text-base font-display shrink-0">
            {player.avatarUrl ? (
              <img
                src={player.avatarUrl}
                alt={player.name}
                className="h-full w-full object-cover"
              />
            ) : (
              initials || <UserIcon size={20} />
            )}
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-display uppercase tracking-tight">
              {player.name}
            </h1>
            <div className="text-xs text-muted-foreground">
              @{player.slug}
              {player.location ? ` · ${player.location}` : ""}
            </div>
          </div>
        </div>
      </header>

      {/* Summary tiles. The headline numbers that answer "is this player
          any good?" at a glance: games, W-L, win %, points-per-round
          (the core skill metric — game length varies, a round doesn't),
          accuracy (pts/game), point diff. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatTile label="Games" value={String(completed.length)} />
        <StatTile
          label="Record"
          value={
            totalGames === 0 ? "—" : `${wins}–${losses}`
          }
        />
        <StatTile
          label="Win rate"
          value={
            totalGames === 0 ? "—" : `${(winPct * 100).toFixed(0)}%`
          }
        />
        <StatTile
          label="Pts/round"
          value={
            scoring && scoring.rounds > 0
              ? (scoring.pointsScored / scoring.rounds).toFixed(1)
              : "—"
          }
        />
        <StatTile
          label="Pts/game"
          value={
            completed.length === 0 ? "—" : accuracy.toFixed(1)
          }
        />
        <StatTile
          label="Point diff"
          value={
            completed.length === 0
              ? "—"
              : `${pointDiff > 0 ? "+" : ""}${pointDiff}`
          }
          tone={
            pointDiff > 0
              ? "positive"
              : pointDiff < 0
                ? "negative"
                : "neutral"
          }
        />
        <StatTile
          label="4-baggers"
          value={scoring ? String(scoring.fourBaggers) : "—"}
        />
        <StatTile
          label="4-bag rate"
          value={
            scoring && scoring.gamesCounted > 0
              ? `${Math.round((scoring.fourBaggers / scoring.gamesCounted) * 100)}%`
              : "—"
          }
        />
      </div>

      {completed.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground text-sm">
            No completed games for this player yet.
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Scoring output — offense/defense per round, unlocked by
              both-sides scoring. Hidden until the player has such
              games (scoring.rounds > 0). */}
          {scoring && scoring.rounds > 0 ? (
            <ScoringOutputCard stats={scoring} />
          ) : null}

          {/* Overall W/L bar — reinforces the summary tile with a
              proportional visual cue. */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Wins vs losses</CardTitle>
              <CardDescription>Across all completed games.</CardDescription>
            </CardHeader>
            <CardContent>
              <div
                className="h-3 rounded-full bg-muted/30 overflow-hidden flex"
                aria-label={`${wins} wins vs ${losses} losses`}
              >
                <div
                  className="bg-primary"
                  style={{ width: `${winPct * 100}%` }}
                />
                <div
                  className="bg-destructive/60"
                  style={{ width: `${(1 - winPct) * 100}%` }}
                />
              </div>
              <div className="mt-2 flex justify-between text-xs text-muted-foreground tabular-nums">
                <span>{wins} wins</span>
                <span>{losses} losses</span>
              </div>
            </CardContent>
          </Card>

          {/* Per-opponent breakdown — like VirtualGameStats but
              including real opponents (humans, doubles teams).
              Top is by volume so the most-played matchups read
              first. */}
          {perOpp.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Head-to-head</CardTitle>
                <CardDescription>
                  W/L by opponent, sorted by games played.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-1.5">
                {perOpp.map((r) => {
                  const total = r.wins + r.losses;
                  const f = total > 0 ? r.wins / total : 0;
                  return (
                    <div
                      key={r.name}
                      className="flex items-center gap-2 text-xs"
                    >
                      <div className="w-32 shrink-0 truncate">{r.name}</div>
                      <div className="flex-1 h-3 rounded-full bg-muted/30 overflow-hidden flex">
                        <div
                          className="bg-primary"
                          style={{ width: `${f * 100}%` }}
                        />
                        <div
                          className="bg-destructive/60"
                          style={{ width: `${(1 - f) * 100}%` }}
                        />
                      </div>
                      <div className="w-14 shrink-0 text-right tabular-nums text-muted-foreground">
                        {r.wins}–{r.losses}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ) : null}

          {/* Accuracy trend — sparkline of pts/game over the
              completed games, oldest → newest. Ceiling at 21 + a
              touch since target scores are typically 21 and the
              eye reads that as "max possible per game". */}
          {accuracySeries.length >= 2 ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Accuracy trend</CardTitle>
                <CardDescription>
                  Points scored per game across the last{" "}
                  {accuracySeries.length} games (oldest left →
                  newest right).
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Sparkline data={accuracySeries} max={25} width={400} height={60} />
              </CardContent>
            </Card>
          ) : null}

          {/* Recent games table linking back into game detail. Mirror
              of the dashboard "recent games" card but scoped to this
              player only. */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-3">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <Trophy size={14} /> Recent games
                </CardTitle>
                <CardDescription>Last {recent.length} completed.</CardDescription>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ul className="divide-y divide-border">
                {recent.map((g) => {
                  const t = teamOf(g);
                  const myPts = t === 1 ? g.team1Score : g.team2Score;
                  const theirPts = t === 1 ? g.team2Score : g.team1Score;
                  const won = g.winningTeam === t;
                  const oppTeam = t === 1 ? g.team2Players : g.team1Players;
                  return (
                    <li key={g.id}>
                      <Link
                        href={`/dashboard/games/${g.id}`}
                        className="flex items-center gap-3 p-3 hover:bg-muted/40 transition-colors"
                      >
                        <div className="shrink-0 w-12 text-xs text-muted-foreground">
                          {new Date(g.playedOn).toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                          })}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm truncate">
                            vs {oppTeam.map((p) => p.name).join(" & ") || "—"}
                            {g.isVirtual ? (
                              <span className="text-[10px] font-display uppercase tracking-wider ml-2 text-muted-foreground bg-muted/30 px-1 py-0.5 rounded">
                                virtual
                              </span>
                            ) : null}
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            {won ? "Win" : "Loss"}
                          </div>
                        </div>
                        <div
                          className={`shrink-0 font-display tabular-nums text-sm px-2 py-1 rounded border border-border ${
                            won ? "bg-primary/10" : "bg-muted/30"
                          }`}
                        >
                          {myPts}–{theirPts}
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

// Offense/defense-per-round card from both-sides scoring. "Scored"
// is the player's side gross, "allowed" the opponent's.
function ScoringOutputCard({ stats }: { stats: PlayerScoringStats }) {
  const off = stats.pointsScored / stats.rounds;
  const def = stats.pointsAllowed / stats.rounds;
  const net = off - def;
  // Trend of points scored per round, per game, oldest → newest.
  const trend = stats.perGame
    .filter((g) => g.rounds > 0)
    .map((g) => g.pointsScored / g.rounds);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Scoring output</CardTitle>
        <CardDescription>
          From both-sides games — {stats.rounds} round
          {stats.rounds === 1 ? "" : "s"} across {stats.gamesCounted} game
          {stats.gamesCounted === 1 ? "" : "s"}.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatTile label="Scored / rd" value={off.toFixed(1)} tone="positive" />
          <StatTile label="Allowed / rd" value={def.toFixed(1)} />
          <StatTile
            label="Net / rd"
            value={`${net > 0 ? "+" : ""}${net.toFixed(1)}`}
            tone={net > 0 ? "positive" : net < 0 ? "negative" : "neutral"}
          />
          <StatTile label="Best round" value={String(stats.bestRound)} />
        </div>
        {trend.length >= 2 ? (
          <div>
            <div className="text-[10px] font-display uppercase tracking-wider text-muted-foreground mb-1">
              Points / round per game (oldest → newest)
            </div>
            {/* Max 12 = a round's ceiling (4 bags in the hole). */}
            <Sparkline data={trend} max={12} width={400} height={60} />
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function StatTile({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string;
  tone?: "positive" | "negative" | "neutral";
}) {
  const valueClass =
    tone === "positive"
      ? "text-primary"
      : tone === "negative"
        ? "text-destructive"
        : "text-foreground";
  return (
    <div className="rounded-md border border-border bg-muted/20 p-3 text-center">
      <div className="text-[10px] font-display uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div
        className={`font-display text-2xl tabular-nums mt-1 ${valueClass}`}
      >
        {value}
      </div>
    </div>
  );
}

// Same sparkline shape used elsewhere (Virtual page). Inline SVG,
// no chart lib. Filled area for visual weight + last-point dot.
function Sparkline({
  data,
  max,
  width,
  height,
}: {
  data: number[];
  max: number;
  width: number;
  height: number;
}) {
  if (data.length < 2) return null;
  const stepX = width / (data.length - 1);
  const points = data
    .map((v, i) => {
      const x = i * stepX;
      const clamped = Math.max(0, Math.min(max, v));
      const y = height - (clamped / max) * height;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
  const area = `${points} ${width},${height} 0,${height}`;
  const lastIdx = data.length - 1;
  const lastV = Math.max(0, Math.min(max, data[lastIdx]!));
  const lx = lastIdx * stepX;
  const ly = height - (lastV / max) * height;
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className="w-full h-16 text-primary"
      role="img"
      aria-label="Points per game over recent games"
    >
      <polygon points={area} fill="currentColor" opacity="0.15" />
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={lx} cy={ly} r="2.5" fill="currentColor" />
    </svg>
  );
}
